import { useEffect, useState } from "react";
import { MessageSquare, Plus, Paperclip } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { useSearchParams } from "react-router-dom";
import { MailboxCard, RecipientSelect, CommunicationActions } from "@/components/communications/DispatchControls";
import { PageHeader, FilterBar } from "@/components/shared/PageChrome";
import { ViewTabs, SearchInput, useListParams } from "@/components/shared/ListChrome";
import { Pagination } from "@/components/shared/StackedList";

const VIEWS = [{ value: "ALL", label: "All" }, { value: "DRAFT", label: "Drafts" }, { value: "REVIEWED", label: "Reviewed" }, { value: "QUEUED", label: "Queued" }, { value: "SENT", label: "Sent" }, { value: "FAILED", label: "Failed" }, { value: "DEAD", label: "Dead" }];

export default function Communications() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [params] = useSearchParams();
  const emptyForm = { entity_type: params.get("entity_type") || "enquiry", entity_id: params.get("entity_id") || "", subject: params.get("subject") || "", body: "", document_id: params.get("document_id") || undefined, to_address: "" };
  const [form, setForm] = useState(emptyForm);
  const [mailbox, setMailbox] = useState(null);
  const { view, q, page, setView, setQ, setPage } = useListParams();

  const load = () => {
    api.get("/communications", { params: { status: view === "ALL" ? undefined : view, q: q || undefined, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/communications/mailbox/status").then((r) => setMailbox(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [view, q, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (params.get("entity_id")) setOpen(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      await api.post("/communications", { ...form, to_address: form.to_address || undefined });
      toast.success("Draft created - review it before queueing");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create draft"); }
  };

  if (error) return <ErrorState testId="communications-error" />;

  return (
    <div data-testid="communications-page" className="space-y-5">
      <PageHeader title="Communications" subtitle="Draft → human review → queue from your own Microsoft 365 mailbox → SENT only when Microsoft accepts it. Delivery receipts are not observed."
        actions={<Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-communication-button" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"><Plus className="mr-1 h-4 w-4" /> New Draft</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New communication draft</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Linked to</Label>
                <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                  <SelectTrigger data-testid="comm-entity-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="enquiry">Enquiry</SelectItem><SelectItem value="booking">Booking</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Entity ID</Label><Input data-testid="comm-entity-id-input" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} placeholder="Paste the ID from the enquiry/booking detail URL" /></div>
              <div><Label>Subject</Label><Input data-testid="comm-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
              <div><Label>Body</Label><Textarea data-testid="comm-body-input" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              <RecipientSelect entityType={form.entity_type} entityId={form.entity_id} value={form.to_address} onChange={(v) => setForm({ ...form, to_address: v })} />
            </div>
            <DialogFooter><Button data-testid="comm-save-button" onClick={submit} disabled={!form.entity_id}>Save draft</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />

      <MailboxCard status={mailbox} onChanged={load} />

      <ViewTabs testId="communications-views" tabs={VIEWS} value={view} onChange={setView} counts={data?.counts} />
      <FilterBar testId="communications-filter-bar" summary={data ? `${data.total} communication${data.total === 1 ? "" : "s"} match` : null}>
        <SearchInput testId="communications-search-input" value={q} onChange={setQ} placeholder="Search subject or recipient…" />
      </FilterBar>

      {!data ? (
        <LoadingState testId="communications-loading" />
      ) : data.items.length === 0 ? (
        <EmptyState testId="communications-empty" icon={MessageSquare} title={view === "ALL" && !q ? "No communications yet" : "No communications match this view"} />
      ) : (
        <div className="space-y-3">
          {data.items.map((c) => (
            <Card key={c.id} data-testid={`communication-row-${c.id}`} className="border-jigsaw-border">
              <CardContent className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-jigsaw-navy">{c.subject || "(no subject)"}</p>
                    <StatusBadge status={c.status} />
                    {c.status === "QUEUED" && <span data-testid={`comm-queued-note-${c.id}`} className="rounded-full bg-jigsaw-warningBg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-jigsaw-warning">Queued — awaiting dispatch{c.attempt_count ? ` · attempt ${c.attempt_count}` : ""}</span>}
                    {c.status === "SENT" && <span data-testid={`comm-sent-note-${c.id}`} className="text-[10px] uppercase tracking-wide text-jigsaw-muted">{c.delivery_note}</span>}
                    {c.to_address && <span className="text-xs text-jigsaw-muted">→ {c.to_name || c.to_address} &lt;{c.to_address}&gt;</span>}
                    {c.last_error && ["FAILED", "DEAD", "UNKNOWN", "QUEUED", "DRAFT"].includes(c.status) && <span data-testid={`comm-error-${c.id}`} className="text-xs text-jigsaw-danger">{c.last_error}</span>}
                  </div>
                  <p className="text-xs text-jigsaw-muted">{c.entity_type} &middot; {c.channel}</p>
                  <p className="mt-1 text-sm text-jigsaw-navy">{c.body}</p>
                  {c.document_id && (
                    <p data-testid={`communication-attachment-${c.id}`} className="mt-1 flex items-center gap-1 text-xs text-jigsaw-orange">
                      <Paperclip className="h-3 w-3" /> {c.document_title || "Attached document"}
                    </p>
                  )}
                </div>
                <CommunicationActions c={c} mailbox={mailbox} onChanged={load} />
              </CardContent>
            </Card>
          ))}
          <Pagination testId="communications-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
